import React, { useState, useMemo, useReducer } from "react";
import dayjs from "dayjs";
import { useFetch } from "../../../Utils/useFetch";
import {
  FETCH_ALL_TRANSACTION_API,
  FETCH_ALL_DEPT_API,
} from "../../../Constants/Apis";
import { reducer } from "../../../Utils/CommonUtils";
import {
  Card,
  Row,
  Col,
  Statistic,
  Select,
  DatePicker,
  Spin,
  Typography,
  Table,
  Empty,
} from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ====== THEME ======
const AXIS_STROKE = "#94a3b8";
const TICK_FILL = "#64748b";
const GRID_STROKE = "#e2e8f0";
const LEGEND_COLOR = "#475569";

const SERIES = {
  sales: "#6366f1",
  ma7: "#10b981",
  tx: "#60a5fa",
  dept: "#a78bfa",
  avg: "#22c55e",
};

const PIE_COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
  "#a4de6c",
  "#d0ed57",
  "#ffc658",
  "#34d399",
];

// ====== HELPERS ======
const formatCurrency = (value) =>
  `Rp ${Number(value || 0).toLocaleString("id-ID", {
    maximumFractionDigits: 0,
  })}`;

const DASHBOARD_AGG_API = `${FETCH_ALL_TRANSACTION_API}/dashboard`;

const DashboardPage = ({ history, userData }) => {
  const initialState = {
    departmentFilter: "",
    dateRange: {
      startDate: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
      endDate: dayjs().format("YYYY-MM-DD"),
    },
  };
  const [state, dispatch] = useReducer(reducer, initialState);

  const [timeRange, setTimeRange] = useState("week");
  const [dateRangePicker, setDateRangePicker] = useState([
    dayjs().subtract(7, "day"),
    dayjs(),
  ]);

  // Build URL with query params so useFetch re-runs automatically
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Jakarta";

  const params = new URLSearchParams({
    startDate: state.dateRange?.startDate || "",
    endDate: state.dateRange?.endDate || "",
    departmentFilter: state.departmentFilter || "",
    counterFilter: "",
    userCounterCode: userData?.counterCode || "",
    tz,
    topNProducts: "10",
  }).toString();

  const url = `${DASHBOARD_AGG_API}?${params}`;

  // Fetch the pre-aggregated dashboard document
  const {
    fetchedData: dashboardData,
    callReFetch: callReFetchTransaction,
    loading: loadingDashboard,
  } = useFetch(url, undefined);
  const dashDoc = dashboardData?.data?.data || null;

  // Fetch department list
  const { fetchedData: { data: deptData = [] } = {}, loading: loadingDept } =
    useFetch(`${FETCH_ALL_DEPT_API}/active`, {});

  // ====== Handlers ======
  const handleDeptFilterChange = (value) => {
    dispatch({ type: "departmentFilter", payload: value || "" });
    callReFetchTransaction();
  };

  const handleDateChange = (dates) => {
    setDateRangePicker(dates || []);
    const [start, end] = dates || [];
    dispatch({
      type: "dateRange",
      payload: {
        startDate: start ? start.format("YYYY-MM-DD") : "",
        endDate: end ? end.format("YYYY-MM-DD") : "",
      },
    });
    callReFetchTransaction();
  };

  const handleQuickRange = (value) => {
    setTimeRange(value || "");
    if (!value) {
      handleDateChange([]);
      return;
    }
    if (value === "yesterday") {
      const y = dayjs().subtract(1, "day");
      handleDateChange([y.startOf("day"), y.endOf("day")]);
    } else if (value === "week") {
      handleDateChange([dayjs().subtract(7, "day"), dayjs()]);
    } else if (value === "month") {
      handleDateChange([dayjs().subtract(30, "day"), dayjs()]);
    } else if (value === "year") {
      handleDateChange([dayjs().subtract(365, "day"), dayjs()]);
    }
  };
  // Compute chart-only series based on backend’s aggregates
  const processed = useMemo(() => {
    // Accept BOTH shapes:
    // A) { kpis:{totalSales,...}, deptPerformance:[...], ... }
    // B) { totalSales, transactionCount, avgTransactionValue, deptPerformance:[...], ... }

    const kpis = dashDoc?.kpis
      ? dashDoc.kpis
      : {
          totalSales: dashDoc?.totalSales ?? 0,
          transactionCount: dashDoc?.transactionCount ?? 0,
          avgTransactionValue: dashDoc?.avgTransactionValue ?? 0,
        };

    const deptPerformance = dashDoc?.deptPerformance || [];
    const categoryPerformance = dashDoc?.categoryPerformance || [];
    const timeSeriesData = dashDoc?.timeSeriesData || [];
    const topProducts = dashDoc?.topProducts || [];
    const noSaleVsOthersSeries = dashDoc?.noSaleVsOthersSeries || [];
    const noSaleTotals = dashDoc?.noSaleTotals || {
      totalNoSale: dashDoc?.totalNoSale ?? 0,
      totalOther: dashDoc?.totalOther ?? 0,
      noSaleShare: dashDoc?.noSaleShare ?? 0,
    };

    // 7-day moving average of sales
    const window = 7;
    const timeSeriesWithMA = timeSeriesData.map((row, idx, arr) => {
      const start = Math.max(0, idx - window + 1);
      const seg = arr.slice(start, idx + 1);
      const sum = seg.reduce((s, r) => s + (r.sales || 0), 0);
      return { ...row, sales_ma7: seg.length ? sum / seg.length : 0 };
    });

    // Avg sales per transaction per day
    const avgPerTransactionTimeSeries = timeSeriesData.map((row) => ({
      date: row.date,
      avg: row.transactions ? row.sales / row.transactions : 0,
    }));

    return {
      totalSales: kpis.totalSales || 0,
      avgTransactionValue: kpis.avgTransactionValue || 0,
      transactionCount: kpis.transactionCount || 0,

      deptPerformance,
      categoryPerformance,
      timeSeriesData,
      timeSeriesWithMA,
      avgPerTransactionTimeSeries,
      topProducts,

      noSaleVsOthersSeries,
      totalNoSale: noSaleTotals.totalNoSale || 0,
      totalOther: noSaleTotals.totalOther || 0,
      noSaleShare: noSaleTotals.noSaleShare || 0,
    };
  }, [dashDoc]);

  const filteredDeptOptions = useMemo(() => {
    const isEcommriUser = userData?.counterCode === "ECOMMRI";
    return (deptData || []).filter((d) => {
      const name = (d?.name || "").trim().toUpperCase();
      if (name === "GUDANG PUSAT") return false;
      if (!isEcommriUser && (name === "ECOMMERCE" || name === "E-COMMERCE"))
        return false;
      return true;
    });
  }, [deptData, userData?.counterCode]);

  const topProductColumns = [
    { title: "Product", dataIndex: "product", key: "product" },
    {
      title: "Quantity Sold",
      dataIndex: "quantity",
      key: "quantity",
      sorter: (a, b) => (a.quantity || 0) - (b.quantity || 0),
    },
    {
      title: "Sales Amount",
      dataIndex: "sales",
      key: "sales",
      render: (sales) => formatCurrency(sales),
      sorter: (a, b) => (a.sales || 0) - (b.sales || 0),
      defaultSortOrder: "descend",
    },
  ];

  // Show the dashboard shell if we have any dataset (even if counts are zero)
  const hasData =
    !!dashDoc &&
    ((processed.timeSeriesData && processed.timeSeriesData.length > 0) ||
      (processed.deptPerformance && processed.deptPerformance.length > 0) ||
      (processed.categoryPerformance &&
        processed.categoryPerformance.length > 0) ||
      (processed.topProducts && processed.topProducts.length > 0) ||
      processed.transactionCount >= 0);

  return (
    <div
      style={{
        width: "100%",
        padding: 20,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflowX: "hidden",
      }}
    >
      <div style={{ width: "100%", maxWidth: 1200, marginBottom: 20 }}>
        <Title level={2} style={{ marginBottom: 0 }}>
          Business Dashboard
        </Title>
        <Text type="secondary">
          Overview of sales performance and business insights
        </Text>

        {/* Filters */}
        <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
          {userData?.counterCode !== "ECOMMRI" && (
            <Col xs={24} sm={8}>
              <Select
                value={state.departmentFilter || undefined}
                style={{ width: "100%" }}
                placeholder="Filter by Department"
                allowClear
                onChange={handleDeptFilterChange}
                loading={loadingDept}
                options={[
                  { label: "All Departments", value: "" },
                  ...filteredDeptOptions.map((dept) => ({
                    label: dept.name,
                    value: dept.name,
                    key: dept._id,
                  })),
                ]}
              />
            </Col>
          )}
          <Col xs={24} sm={8}>
            <RangePicker
              style={{ width: "100%" }}
              onChange={handleDateChange}
              value={Array.isArray(dateRangePicker) ? dateRangePicker : []}
              allowClear={false}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Select
              value={timeRange || undefined}
              style={{ width: "100%" }}
              allowClear
              onChange={handleQuickRange}
              placeholder="Quick range"
              options={[
                { value: "yesterday", label: "Yesterday" },
                { value: "week", label: "Last 7 Days" },
                { value: "month", label: "Last 30 Days" },
                { value: "year", label: "Last 365 Days" },
              ]}
            />
          </Col>
        </Row>
      </div>

      {loadingDashboard ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 300,
            width: "100%",
          }}
        >
          <Spin size="large" />
        </div>
      ) : !hasData ? (
        <div
          style={{
            width: "100%",
            maxWidth: 1200,
            background: "#fff",
            borderRadius: 8,
            padding: 24,
          }}
        >
          <Empty description="No transactions found for the selected filters." />
        </div>
      ) : (
        <>
          {/* KPIs */}
          <Row
            gutter={[16, 16]}
            style={{ width: "100%", maxWidth: 1200, marginBottom: 20 }}
          >
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Total Sales"
                  value={processed.totalSales}
                  precision={0}
                  formatter={(v) => formatCurrency(v)}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Average Transaction Value"
                  value={processed.avgTransactionValue}
                  precision={0}
                  formatter={(v) => formatCurrency(v)}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Card.Grid style={{ width: "50%", boxShadow: "none" }}>
                  <Statistic
                    title="Total Transactions"
                    value={processed.transactionCount}
                    precision={0}
                  />
                </Card.Grid>
                <Card.Grid style={{ width: "50%", boxShadow: "none" }}>
                  <Statistic
                    title="Total No Sale"
                    value={processed.totalNoSale}
                    precision={0}
                  />
                </Card.Grid>
              </Card>
            </Col>
          </Row>

          {/* Sales Trend (with 7d MA) */}
          <div
            style={{
              width: "100%",
              maxWidth: 1200,
              marginBottom: 20,
              backgroundColor: "#fff",
              padding: 20,
              borderRadius: 8,
            }}
          >
            <Title level={4}>Sales Trend</Title>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart
                data={processed.timeSeriesWithMA}
                margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
              >
                <CartesianGrid stroke={GRID_STROKE} strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  stroke={AXIS_STROKE}
                  tick={{ fill: TICK_FILL }}
                />
                <YAxis
                  stroke={AXIS_STROKE}
                  tick={{ fill: TICK_FILL }}
                  tickFormatter={formatCurrency}
                />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  labelStyle={{ color: LEGEND_COLOR }}
                  itemStyle={{ color: LEGEND_COLOR }}
                />
                <Legend wrapperStyle={{ color: LEGEND_COLOR }} />
                <Line
                  type="monotone"
                  dataKey="sales"
                  stroke={SERIES.sales}
                  name="Daily Sales"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="sales_ma7"
                  stroke={SERIES.ma7}
                  name="7-Day Avg"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Transaction Volume */}
          <div
            style={{
              width: "100%",
              maxWidth: 1200,
              marginBottom: 20,
              backgroundColor: "#fff",
              padding: 20,
              borderRadius: 8,
            }}
          >
            <Title level={4}>Transaction Volume Trend</Title>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={processed.timeSeriesData}
                margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
              >
                <CartesianGrid stroke={GRID_STROKE} strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  stroke={AXIS_STROKE}
                  tick={{ fill: TICK_FILL }}
                />
                <YAxis stroke={AXIS_STROKE} tick={{ fill: TICK_FILL }} />
                <Tooltip
                  labelStyle={{ color: LEGEND_COLOR }}
                  itemStyle={{ color: LEGEND_COLOR }}
                />
                <Legend wrapperStyle={{ color: LEGEND_COLOR }} />
                <Bar
                  dataKey="transactions"
                  name="Transactions"
                  fill={SERIES.tx}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Dept & Category */}
          <Row
            gutter={[16, 16]}
            style={{ width: "100%", maxWidth: 1200, marginBottom: 20 }}
          >
            <Col xs={24} lg={12}>
              <div
                style={{
                  backgroundColor: "#fff",
                  padding: 20,
                  borderRadius: 8,
                  height: "100%",
                }}
              >
                <Title level={4}>Department Performance</Title>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={processed.deptPerformance}
                    margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid stroke={GRID_STROKE} strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      stroke={AXIS_STROKE}
                      tick={{ fill: TICK_FILL }}
                    />
                    <YAxis stroke={AXIS_STROKE} tick={{ fill: TICK_FILL }} />
                    <Tooltip
                      formatter={(v) => formatCurrency(v)}
                      labelStyle={{ color: LEGEND_COLOR }}
                      itemStyle={{ color: LEGEND_COLOR }}
                    />
                    <Legend wrapperStyle={{ color: LEGEND_COLOR }} />
                    <Bar dataKey="sales" name="Sales" fill={SERIES.dept} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div
                style={{
                  backgroundColor: "#fff",
                  padding: 20,
                  borderRadius: 8,
                  height: "100%",
                }}
              >
                <Title level={4}>Category Distribution</Title>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={processed.categoryPerformance}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={90}
                      dataKey="sales"
                      nameKey="name"
                      label={({ name, percent }) =>
                        `${name}: ${
                          Number.isFinite(percent)
                            ? (percent * 100).toFixed(0)
                            : 0
                        }%`
                      }
                    >
                      {(processed.categoryPerformance || []).map((_, idx) => (
                        <Cell
                          key={idx}
                          fill={PIE_COLORS[idx % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v) => formatCurrency(v)}
                      labelStyle={{ color: LEGEND_COLOR }}
                      itemStyle={{ color: LEGEND_COLOR }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Col>
          </Row>

          {/* Avg Sales per Tx */}
          <div
            style={{
              width: "100%",
              maxWidth: 1200,
              marginBottom: 20,
              backgroundColor: "#fff",
              padding: 20,
              borderRadius: 8,
            }}
          >
            <Title level={4}>Avg Sales per Transaction Trend</Title>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={processed.avgPerTransactionTimeSeries}
                margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
              >
                <CartesianGrid stroke={GRID_STROKE} strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  stroke={AXIS_STROKE}
                  tick={{ fill: TICK_FILL }}
                />
                <YAxis
                  stroke={AXIS_STROKE}
                  tick={{ fill: TICK_FILL }}
                  tickFormatter={formatCurrency}
                />
                <Tooltip
                  formatter={(v) => formatCurrency(v)}
                  labelStyle={{ color: LEGEND_COLOR }}
                  itemStyle={{ color: LEGEND_COLOR }}
                />
                <Legend wrapperStyle={{ color: LEGEND_COLOR }} />
                <Line
                  type="monotone"
                  dataKey="avg"
                  name="Avg Sales/Transaction"
                  stroke={SERIES.avg}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* NoSale vs Others */}
          <div
            style={{
              width: "100%",
              maxWidth: 1200,
              marginBottom: 20,
              backgroundColor: "#fff",
              padding: 20,
              borderRadius: 8,
            }}
          >
            <Title level={4}>NoSale vs Other Transactions (Daily Count)</Title>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={processed.noSaleVsOthersSeries || []}
                margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
              >
                <CartesianGrid stroke={"#e2e8f0"} strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  stroke={"#94a3b8"}
                  tick={{ fill: "#64748b" }}
                />
                <YAxis stroke={"#94a3b8"} tick={{ fill: "#64748b" }} />
                <Tooltip
                  labelStyle={{ color: "#475569" }}
                  itemStyle={{ color: "#475569" }}
                />
                <Legend wrapperStyle={{ color: "#475569" }} />
                <Bar
                  dataKey="noSaleCount"
                  name="NoSale"
                  stackId="a"
                  fill="#f59e0b"
                />
                <Bar
                  dataKey="otherCount"
                  name="Other"
                  stackId="a"
                  fill="#60a5fa"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Top Products */}
          <div
            style={{
              width: "100%",
              maxWidth: 1200,
              marginBottom: 20,
              backgroundColor: "#fff",
              padding: 20,
              borderRadius: 8,
            }}
          >
            <Title level={4}>Top Selling Products</Title>
            <Table
              columns={topProductColumns}
              dataSource={processed.topProducts}
              rowKey="product"
              pagination={{ pageSize: 10, showSizeChanger: true }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
